/**
 * Response Card Component
 * Displays a single response to an audit finding
 */

import { Paper, Group, Badge, Text, Stack } from '@mantine/core';
import {
  IconMessage,
  IconClipboardCheck,
  IconProgress,
  IconCheck,
  IconMessageCircle,
} from '@tabler/icons-react';
import { AuditFindingResponse, ResponseType } from '../../types/audit';
import { format } from 'date-fns';

interface ResponseCardProps {
  response: AuditFindingResponse;
}

export function ResponseCard({ response }: ResponseCardProps) {
  const getResponseTypeIcon = (type: ResponseType) => {
    switch (type) {
      case 'acknowledgment':
        return <IconClipboardCheck size={16} />;
      case 'action_plan':
        return <IconMessage size={16} />;
      case 'progress_update':
        return <IconProgress size={16} />;
      case 'completion_evidence':
        return <IconCheck size={16} />;
      case 'comment':
        return <IconMessageCircle size={16} />;
      default:
        return <IconMessage size={16} />;
    }
  };

  const getResponseTypeColor = (type: ResponseType): string => {
    const colors = {
      acknowledgment: 'blue',
      action_plan: 'yellow',
      progress_update: 'cyan',
      completion_evidence: 'green',
      comment: 'gray',
    };
    return colors[type];
  };

  const formatResponseType = (type: ResponseType): string => {
    return type
      .split('_')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  return (
    <Paper p="sm" withBorder style={{ backgroundColor: 'var(--mantine-color-gray-light)' }}>
      <Stack gap="xs">
        {/* Header */}
        <Group justify="space-between" align="flex-start">
          <Group gap="xs">
            <Badge
              color={getResponseTypeColor(response.response_type)}
              variant="light"
              leftSection={getResponseTypeIcon(response.response_type)}
              size="sm"
            >
              {formatResponseType(response.response_type)}
            </Badge>
          </Group>
          <Text size="xs" c="dimmed">
            {format(new Date(response.created_at), 'MMM d, yyyy h:mm a')}
          </Text>
        </Group>

        {/* Response Text */}
        <Text size="sm">{response.response_text}</Text>

        {/* Action Plan Details */}
        {response.action_plan && (
          <Paper p="xs" withBorder style={{ backgroundColor: 'white' }}>
            <Text size="xs" fw={600} c="dimmed" mb={4}>
              Action Plan
            </Text>
            <Text size="sm">{response.action_plan}</Text>

            {response.responsible_person && (
              <Text size="xs" c="dimmed" mt="xs">
                Responsible: {response.responsible_person}
              </Text>
            )}

            {response.target_completion_date && (
              <Text size="xs" c="dimmed">
                Target Date: {format(new Date(response.target_completion_date), 'MMM d, yyyy')}
              </Text>
            )}
          </Paper>
        )}

        {/* Evidence Details */}
        {response.evidence_description && (
          <Paper p="xs" withBorder style={{ backgroundColor: 'white' }}>
            <Text size="xs" fw={600} c="dimmed" mb={4}>
              Evidence of Completion
            </Text>
            <Text size="sm">{response.evidence_description}</Text>

            {response.actual_completion_date && (
              <Text size="xs" c="dimmed" mt="xs">
                Completed: {format(new Date(response.actual_completion_date), 'MMM d, yyyy')}
              </Text>
            )}
          </Paper>
        )}

        {/* Footer - Responder Info */}
        <Group justify="space-between">
          <Text size="xs" c="dimmed">
            By: {response.responded_by_name}
            {response.responded_by_role && ` (${response.responded_by_role})`}
          </Text>
        </Group>
      </Stack>
    </Paper>
  );
}
